import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { IPrintableReportHeading } from '@models/report-print-heading';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import getComputedStyle from '@popperjs/core/lib/dom-utils/getComputedStyle';
import {
  AssessmentStatus,
} from '@shared/branded-reports/branded-individual-student-over-time-report-detail/student-over-time-report-detail.model';
import { ProgressReportType } from '@shared/enums/progress-report-type';
import { RoleType } from '@shared/enums/role-type';
import { ScoreBenchmark } from '@shared/enums/score-benchmark';
import { TopicType } from '@shared/enums/topic-type';
import { catchAndRedirectNonErrorToMainFlow } from '@shared/zb-rxjs-operators/rxjs-operators';
import { ZbpDataTableComponent } from '@shared/zbp-data-table/zbp-data-table.component';
import { ZbpDataTable } from '@shared/zbp-data-table/zbp-data-table.model';
import { BehaviorSubject, from, mergeMap, Subscription } from 'rxjs';
import { ScoreDetailModalComponent } from '../../modals/score-detail-modal/score-detail-modal.component';
import { BrandedReportsHelpers } from '../branded-reports-helpers';

import { ClassSkillsHeatmapReport, ClassSkillsHeatmapStudent } from './class-skills-heatmap-report-detail.model';
import SIMPLE_COLUMN = ZbpDataTable.SIMPLE_COLUMN;
import TEXT_CELL = ZbpDataTable.TEXT_CELL;
import PILL_CELL = ZbpDataTable.PILL_CELL;
import LINK_CELL = ZbpDataTable.LINK_CELL;

interface BrandVars {
  fontFamily: string;
  tableBorderColor: string;
  tableDarkBackground: string;
  white: string;
  black: string;
  primary: string;
  secondary: string;
  red300: string;
  red500: string;
  cream300: string;
  cream500: string;
  green300: string;
  green500: string;
  neutralsWarmGrey200: string;
  neutralsWarmGreyW40: string;
  neutralsWarmGreyW80: string;
  neutralsCoolGrey200: string;
  neutralsCoolGrey400: string;
}

/**
 * Uses HTML `table` for report
 *
 * Whenever updating HTML/CSS, print should be tested to ensure multi-page reports print correctly.
 */
@Component({
  selector: 'zbp-branded-class-skills-heatmap-report-detail',
  imports: [
    ZbpDataTableComponent
  ],
  templateUrl: './branded-class-skills-heatmap-report-detail.component.html',
  styleUrl: './branded-class-skills-heatmap-report-detail.component.scss'
})
export class BrandedClassSkillsHeatmapReportDetailComponent implements OnInit, OnChanges, OnDestroy {

  @Input() showProgressReportsHelp: boolean = false;
  @Input({ required: true }) productName: string;
  @Input({ required: true }) className: string;
  @Input({ required: true }) classSkillsHeatmapReport?: ClassSkillsHeatmapReport;
  @Input({ required: true }) variant: string;
  @Input({ required: true }) role: RoleType;
  @Output() refreshData = new EventEmitter();
  @Input() isPrinting: boolean;
  @Input() printableReportHeadings: IPrintableReportHeading[];

  columns: ZbpDataTable.Column[] = [];
  /** Each array of columns will be used to generate a report table. Includes Students column.*/
  columnsForPrint: ZbpDataTable.Column[][] = [];
  rows: ZbpDataTable.Row[] = [];
  /** Each array of rows will be used to generate a report table. */
  rowsForPrinting: ZbpDataTable.Row[][] = [];

  readonly OBJECTIVE_COLUMNS_PER_PRINT_PAGE = 6;
  readonly STUDENTS_COLUMN_KEY = 'students';

  private brandVars$ = new BehaviorSubject<BrandVars>(null);
  private subscriptions = new Subscription();

  constructor(
    private hostElement: ElementRef,
    private modalService: NgbModal,
    private translateService: TranslateService,
  ) {
  }

  ngOnInit() {
    // load brand/theme styles
    const computedStyle = getComputedStyle(this.hostElement.nativeElement);
    const fontFamily = computedStyle.getPropertyValue('--brand-primary-font');
    const tableBorderColor = computedStyle.getPropertyValue('--brand-table-border');
    const tableDarkBackground = computedStyle.getPropertyValue('--brand-table-dark-background');
    const white = computedStyle.getPropertyValue('--brand-white');
    const black = computedStyle.getPropertyValue('--brand-black');
    const primary = computedStyle.getPropertyValue('--brand-primary');
    const secondary = computedStyle.getPropertyValue('--brand-secondary');
    const red300 = computedStyle.getPropertyValue('--brand-red-300');
    const red500 = computedStyle.getPropertyValue('--brand-red-500');
    const cream300 = computedStyle.getPropertyValue('--brand-cream-300');
    const cream500 = computedStyle.getPropertyValue('--brand-cream-500');
    const green300 = computedStyle.getPropertyValue('--brand-green-300');
    const green500 = computedStyle.getPropertyValue('--brand-green-500');
    const neutralsWarmGrey200 = computedStyle.getPropertyValue('--brand-neutrals-warm-grey-200');
    const neutralsWarmGreyW40 = computedStyle.getPropertyValue('--brand-neutrals-warm-grey-W40');
    const neutralsWarmGreyW80 = computedStyle.getPropertyValue('--brand-neutrals-warm-grey-W80');
    const neutralsCoolGrey200 = computedStyle.getPropertyValue('--brand-neutrals-cool-grey-200');
    const neutralsCoolGrey400 = computedStyle.getPropertyValue('--brand-neutrals-cool-grey-400');

    this.brandVars$.next({
      fontFamily,
      tableBorderColor,
      tableDarkBackground,
      white,
      black,
      primary,
      secondary,
      red300,
      red500,
      cream300,
      cream500,
      green300,
      green500,
      neutralsWarmGrey200,
      neutralsWarmGreyW40,
      neutralsWarmGreyW80,
      neutralsCoolGrey200,
      neutralsCoolGrey400,
    });

    this.columns = this.buildColumns();
    this.rows = this.buildRows();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['classSkillsHeatmapReport'] && this.classSkillsHeatmapReport) {
      this.columns = this.buildColumns();
      this.rows = this.buildRows();
    }

    // When this input changes to true, the print function is executed.
    // The caller is responsible for resetting the page after print is complete through window.onafterprint.
    if (changes.isPrinting && this.isPrinting) {
      this.print();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  buildColumns(): ZbpDataTable.Column[] {
    const newColumns: ZbpDataTable.Column[] = [
      {
        type: SIMPLE_COLUMN,
        key: this.STUDENTS_COLUMN_KEY,
        header: 'Students',
        textAlign: 'center',
      },
    ];

    this.classSkillsHeatmapReport.objectives?.forEach((objective) => {
      newColumns.push({
        type: SIMPLE_COLUMN,
        key: objective,
        header: objective,
        textAlign: 'center',
      });
    });

    return newColumns;
  }

  buildRows() {
    const rows = [];

    this.classSkillsHeatmapReport.students?.forEach((student) => {
      rows.push(this.getStudentRow(student));
    });

    return rows;
  }

  getStudentRow(student) {
    const newCells: Record<string, ZbpDataTable.Cell> = {
      [this.STUDENTS_COLUMN_KEY]: {
        type: TEXT_CELL,
        text: `${student.lastName}, ${student.firstName}`,
        markRowHeader: true,
        borderRight: `solid 1px var(--brand-neutrals-warm-grey-W80)`,
        textAlign: 'right',
        // @todo The student name should link to individual student skills over time report. ZBP-17929.
        // onClick: () => {},
      },
    };

    if (student.assessmentStatus === AssessmentStatus.Completed) {
      this.classSkillsHeatmapReport.objectives.forEach((objective) => {
        const studentObjective = student.objectives.find(x => x.objective === objective);
        if (!studentObjective) {
          console.error(`Data Error: cannot find objective "${objective}" for student:`, student);
        }
        const colors = this.getColors(studentObjective?.overallBenchmark);
        newCells[objective] = {
          type: PILL_CELL,
          text: studentObjective?.overallScore ?? 'ERROR',
          backgroundColor: colors.background,
          borderColor: colors.border,
          justifyItems: 'center',
          onClick: () => this.handleScoreLink(student),
        };
      });
    } else if (student.assessmentStatus === AssessmentStatus.InProgress) {
      if (this.classSkillsHeatmapReport?.objectives?.length > 0) {
        const firstKey = this.classSkillsHeatmapReport.objectives[0];
        newCells[firstKey] = {
          type: TEXT_CELL,
          text: 'In progress',
          textAlign: 'center',
          backgroundColor: 'var(--brand-neutrals-warm-grey-W40)',
          columnSpan: this.classSkillsHeatmapReport.objectives.length,
        };
      }
    } else if (student.assessmentStatus === AssessmentStatus.AddScore) {
      if (this.classSkillsHeatmapReport?.objectives?.length > 0) {
        const firstKey = this.classSkillsHeatmapReport.objectives[0];
        newCells[firstKey] = {
          type: LINK_CELL,
          text: '+ Add Score',
          textAlign: 'center',
          onClick: () => this.handleScoreLink(student),
          theme: 'primaryText',
          columnSpan: this.classSkillsHeatmapReport.objectives.length,
        };
      }
    } else if (student.assessmentStatus === AssessmentStatus.ReadyForScoring) {
      if (this.classSkillsHeatmapReport?.objectives?.length > 0) {
        const firstKey = this.classSkillsHeatmapReport.objectives[0];
        newCells[firstKey] = {
          type: LINK_CELL,
          text: '+ Ready for Scoring',
          textAlign: 'center',
          onClick: () => this.handleScoreLink(student),
          theme: 'primaryText',
          columnSpan: this.classSkillsHeatmapReport.objectives.length,
        };
      }
    } else if (student.assessmentStatus === AssessmentStatus.NotStarted) {
      if (this.classSkillsHeatmapReport?.objectives?.length > 0) {
        const firstKey = this.classSkillsHeatmapReport.objectives[0];
        newCells[firstKey] = {
          type: TEXT_CELL,
          text: 'Not started',
          textAlign: 'center',
          backgroundColor: 'var(--brand-neutrals-warm-grey-W40)',
          columnSpan: this.classSkillsHeatmapReport.objectives.length,
        };
      }
    } else if (student.assessmentStatus === AssessmentStatus.PartiallyScored) {
      this.classSkillsHeatmapReport.objectives.forEach((objective) => {
        const studentObjective = student.objectives.find(x => x.objective === objective);
        if (!studentObjective) {
          console.error(`Data Error: cannot find objective "${objective}" for student:`, student);
        }
        let text: string;
        if (studentObjective.needsScored) {
          text = '+ ADD'
        } else {
          text = studentObjective?.overallScore ?? 'ERROR';
        }
        const colors = this.getColors(
          studentObjective?.overallBenchmark,
          studentObjective.needsScored
        );
        newCells[objective] = {
          text,
          type: PILL_CELL,
          backgroundColor: colors.background,
          borderColor: colors.border,
          textColor: colors.text,
          justifyItems: 'center',
          onClick: () => this.handleScoreLink(student),
        };
      });
    }
    return { cells: newCells };
  }

  getColors(benchmark: ScoreBenchmark, needsScored: boolean = false): {
    background: string,
    border: string,
    text: string,
  } {
    if (needsScored) {
      return {
        background: 'var(--brand-primary)',
        border: 'var(--brand-primary)',
        text: 'var(--brand-white)',
      };
    }
    switch (benchmark) {
    case ScoreBenchmark.DoesNotMeet:
      return {
        background: 'var(--brand-red-300)',
        border: 'var(--brand-red-500)',
        text: 'var(--brand-black)',
      };
    case ScoreBenchmark.Meets:
      return {
        background: 'var(--brand-cream-300)',
        border: 'var(--brand-cream-500)',
        text: 'var(--brand-black)',
      };
    case ScoreBenchmark.Exceeds:
      return {
        background: 'var(--brand-green-300)',
        border: 'var(--brand-green-500)',
        text: 'var(--brand-black)',
      };
    default:
      return {
        background: 'var(--brand-white)',
        border: 'var(--brand-secondary)',
        text: 'var(--brand-black)',
      };
    }
  }

  /**
   * Handles clicking on the score.
   *
   * The user will be directed to the score detail modal.
   */
  handleScoreLink(report: ClassSkillsHeatmapStudent): void {
    const {
      firstName,
      lastName,
      score,
      completedOn,
      lastOverriddenUserName,
      lastOverriddenOn,
      wordListLevel = ScoreDetailModalComponent.translateWordListLevel(report.wordListLevel, this.translateService),
    } = report;

    const modalRefObservable = from(ScoreDetailModalComponent.openAssessment(this.modalService, {
      studentName: `${lastName}, ${firstName}`,
      studentId: report.userId,
      classroomId: this.classSkillsHeatmapReport?.classroomId,
      className: this.className,
      productName: this.productName,
      activityName: this.classSkillsHeatmapReport?.assessmentName,
      productType: this.classSkillsHeatmapReport?.productType,
      variant: this.classSkillsHeatmapReport?.grade,
      assessmentKey: this.classSkillsHeatmapReport?.assessmentKey,
      assessmentStatus: report?.assessmentStatus,
      score,
      scoreIsOverridden: report?.scoreIsOverridden,
      parts: report.scoreDetail,
      topicType: TopicType.Assessment,
      reportName: ProgressReportType.ClassSkillsHeatmap,
      allowEdit: this.role === RoleType.Teacher,
      completedOn,
      lastOverriddenUserName,
      lastOverriddenOn,
      wordListLevel,
    })).pipe(
      mergeMap(ref => ref.result),
      catchAndRedirectNonErrorToMainFlow(),
    );

    this.subscriptions.add(modalRefObservable.subscribe((res) => {
      if (res?.refreshData) {
        this.refreshData.emit();
      }
    }));
  }

  /**
   * Prints the report.
   *
   * Triggered by changes to the {@link BrandedClassSkillsHeatmapReportDetailComponent.isPrinting} property.
   *
   * Creates multiple table reports with columns limited per page.
   * This is to avoid a columns being cut off while printing.
   *
   * Sets a column limit per report
   * with {@link BrandedClassSkillsHeatmapReportDetailComponent.OBJECTIVE_COLUMNS_PER_PRINT_PAGE}.
   *
   * Print is configured for A4 Landscape size in this component's SCSS file.
   * Headers are added at the top of each page through the table element.
   *
   * For testing, if necessary, can copy {@link BrandedClassSkillsHeatmapReportDetailComponent.columns} multiple times
   * before grouping the results for multi-page printing.
   */
  print(): void {
    // Separate Students column from Objective columns.
    const studentsColumn = this.columns.find(col => col.key === this.STUDENTS_COLUMN_KEY);
    const objectiveColumns = this.columns.filter(col => col.key !== this.STUDENTS_COLUMN_KEY);

    const groupedObjectiveColumns = BrandedReportsHelpers
      .groupReportResultsByAmount(objectiveColumns, this.OBJECTIVE_COLUMNS_PER_PRINT_PAGE);

    // Create column groups rendered for printing with Students column always being first.
    this.columnsForPrint = groupedObjectiveColumns.map(objectiveGroup => [
      studentsColumn,
      ...objectiveGroup
    ]);

    // Create separate row sets for each rendered table.
    // Each rendered table will start with the 'Students' column.
    // Cells that span (e.g. Need Scoring, Partially Scored etc)
    // are assigned to the 1st objective column and spans the width of the table
    this.rowsForPrinting = this.columnsForPrint.map((columnGroup, /* tableIndex */) => {
      const tableRows: ZbpDataTable.Row[] = [];

      this.rows.forEach((row) => {
        // Check if this is a single-cell spanning row by looking for a cell with columnSpan
        const hasSpanningCell = Object.values(row.cells).some(cell => cell.columnSpan && cell.columnSpan > 1);

        if (hasSpanningCell) {
          // Single-cell spanning row (In progress, Not started, Add Score, etc.)

          // Find the first objective column in this table (skip the 'Students' column)
          const firstObjectiveColumn = columnGroup.find(col => col.key !== this.STUDENTS_COLUMN_KEY);

          if (firstObjectiveColumn) {
            // Calculate the number of objective columns in this table
            const objectiveColumnsCount = columnGroup.length - 1;

            // Find the original spanning cell (not the Students cell)
            const originalSpanningCell = Object.entries(row.cells)
              .find(([key, cell]) => key !== this.STUDENTS_COLUMN_KEY && cell.columnSpan && cell.columnSpan > 1)?.[1];

            if (originalSpanningCell) {
              const newCells: Record<string, ZbpDataTable.Cell> = {
                students: row.cells.students,
                // Map the spanning cell to the first objective column of this table
                [firstObjectiveColumn.key]: {
                  ...originalSpanningCell,
                  columnSpan: objectiveColumnsCount
                }
              };

              tableRows.push({ cells: newCells });
            }
          }
        } else {
          // Regular row with multiple cells (completed assessments with scores)
          // Filter cells to only include those that match the current table's columns
          const filteredCells: Record<string, ZbpDataTable.Cell> = {};

          columnGroup.forEach((column) => {
            if (row.cells[column.key]) {
              filteredCells[column.key] = row.cells[column.key];
            }
          });

          tableRows.push({ cells: filteredCells });
        }
      });

      return tableRows;
    });

    // Give time for Tables and Printable Header to render
    setTimeout(() => {
      window.print();
    }, 2000);
  }
}
